// PowerSync клиент — жизненный цикл подключения (connect / disconnect / смена
// владельца локальной базы).
//
// Сам инстанс БД живёт в `db-instance.ts` (лист графа импортов) и
// реэкспортируется отсюда, как и `readPowerSyncOwner` из `owner.ts`: доступ к
// SQLite нужен модулям восстановления, которые обязаны быть НИЖЕ коннектора в
// графе, иначе получается кольцо client → connector → upload-failures.
import { SyncStreamConnectionMethod } from "@powersync/common";
import { StudyHubConnector } from "./connector.js";
import { getPowerSync, peekPowerSync } from "./db-instance.js";
import { resetPowerSyncCredentials, resumePowerSyncAuth } from "./jwt-provider.js";
import { readPowerSyncOwner, writePowerSyncOwner } from "./owner.js";

let _connectPromise: Promise<void> | null = null;

export { getPowerSync, peekPowerSync } from "./db-instance.js";
export { readPowerSyncOwner } from "./owner.js";

function readOwner(): string | null {
	return readPowerSyncOwner();
}

function writeOwner(userId: string): void {
	writePowerSyncOwner(userId);
}

/**
 * Подключение от имени конкретного пользователя — единственная точка входа
 * после login.
 *
 * Если локальная база принадлежит другому аккаунту, она стирается ДО connect:
 * иначе новый пользователь получает чужой снапшот в SQLite (его собственные
 * запросы фильтруют по `user_id` и показывают пустоту) и, что хуже, кэш
 * PowerSync-JWT прошлого владельца — сервис отвергает такой токен, SDK уходит
 * в вечный retry, а UI остаётся с незатухающей полоской синхронизации.
 */
export async function connectPowerSyncForUser(userId: string): Promise<void> {
	const owner = readOwner();
	if (owner && owner !== userId) {
		resetPowerSyncCredentials();
		await disconnectAndClearPowerSync().catch(() => undefined);
	}
	writeOwner(userId);
	// Снимаем «синк остановлен» ровно здесь — это единственная точка, где сессия
	// подтверждена (провайдер зовёт нас только на подтверждённого пользователя).
	resumePowerSyncAuth();
	return connectPowerSync();
}

/**
 * Подключение к PowerSync Service. Вызывать после успешного login,
 * до этого PowerSync работает offline (read-only из локальной SQLite).
 *
 * Для первого подключения после входа использовать `connectPowerSyncForUser` —
 * здесь проверки владельца нет (это путь реконнекта уже подключённой сессии).
 */
export function connectPowerSync(): Promise<void> {
	if (_connectPromise) return _connectPromise;
	// Паузу аутентификации здесь НЕ снимаем. Этот путь — реконнект: его зовут
	// восстановление после возврата вкладки из фона / offline→online и кнопка
	// «Повторить», и ни один из них не знает, есть ли сессия. Пока он снимал
	// стоп, гость на публичной странице получал вечный цикл 401 (см.
	// `jwt-provider`, инцидент 2026-08-22). Разрешение выдавать токены даёт
	// только `connectPowerSyncForUser`.
	void requestPersistentStorage();
	// HTTP-стриминг вместо дефолтного WebSocket: WS-апгрейд к
	// powersync.study.iec.exchange через Caddy/проксирующие сети (в т.ч. РФ-сети)
	// часто не проходит → клиент молча не синкается, страницы на usePowerSyncQuery
	// висят в скелетонах навсегда. HTTP-стриминг проверен рабочим end-to-end
	// (POST /sync/stream → 200 + checkpoint) и стабильнее проходит через прокси.
	_connectPromise = getPowerSync()
		.connect(new StudyHubConnector(), {
			connectionMethod: SyncStreamConnectionMethod.HTTP,
		})
		.catch((err: unknown) => {
			// Не кэшируем отказ: иначе провалившийся первый connect возвращался бы
			// из memo вечно, и восстановиться можно было только перезагрузкой.
			_connectPromise = null;
			throw err;
		});
	return _connectPromise;
}

/**
 * Отключение БЕЗ очистки локальных данных.
 *
 * Используется при потере сессии (протух refresh, сеть отдала 401). Раньше этот
 * путь звал `disconnectAndClear()` и вместе с локальной SQLite уничтожал
 * upload-очередь: всё, что пользователь наработал офлайн, исчезало без следа и
 * без предупреждения. Данные должны пережить повторный вход.
 */
export async function disconnectPowerSync(): Promise<void> {
	_connectPromise = null;
	await peekPowerSync()?.disconnect();
}

/**
 * Отключение С очисткой локальной БД — для ЯВНОГО выхода из аккаунта и для
 * входа под другим пользователем: следующий владелец устройства не должен
 * увидеть чужие данные.
 *
 * Здесь `getPowerSync()`, а не `peekPowerSync()?.` — инстанса в памяти может не быть
 * (холодный старт, вход сразу после загрузки страницы), но файл SQLite с
 * данными прошлого владельца лежит на диске и переживает перезагрузку. Тогда
 * `peek` молча ничего не делал бы, а следующий `connect()` открыл бы ту же базу.
 */
export async function disconnectAndClearPowerSync(): Promise<void> {
	_connectPromise = null;
	await getPowerSync().disconnectAndClear();
}

/** Есть ли неотправленные локальные изменения. */
export async function hasPendingUploads(): Promise<boolean> {
	const batch = await peekPowerSync()?.getCrudBatch(1);
	return (batch?.crud.length ?? 0) > 0;
}

/** Сколько операций ждёт отправки (для индикатора; потолок — размер батча). */
export async function pendingUploadCount(): Promise<number> {
	const batch = await peekPowerSync()?.getCrudBatch(100);
	return batch?.crud.length ?? 0;
}

/**
 * Запрос persistent-хранилища. Без него IndexedDB (локальная SQLite + очереди
 * outbox) живёт в best-effort bucket: Safari чистит его после 7 дней без
 * визита, Chrome — под давлением на диск. Для offline-first это прямая потеря
 * неотправленных правок, поэтому просим постоянное хранение при первом connect.
 */
async function requestPersistentStorage(): Promise<void> {
	try {
		if (typeof navigator === "undefined" || !navigator.storage?.persist) return;
		if (await navigator.storage.persisted()) return;
		await navigator.storage.persist();
	} catch {
		// Браузер может отказать без объяснения — это не повод ломать connect.
	}
}
